/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { flatMap, keyBy, union } from 'lodash';
import { compose, withProps } from 'recompose';

import { Grid, InlineNotification, PageGridContainer, useTheme } from '@coursera/cds-core';
import { withResponsiveConfig } from '@coursera/coursera-ui';

import filterExistsOrDefault from 'bundles/program-common/utils/filterExistsOrDefault';
import { Heading, Section } from 'bundles/program-home/components/AutoHeading';
import MultiProgramSkillsetWarningBanner from 'bundles/program-home/components/enterprise-home/MultiProgramSkillsetWarningBanner';
import type {
  EnterpriseHomePageQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_enterprisePrograms_elements as EnterpriseProgram,
} from 'bundles/program-home/components/enterprise-home/__generated__/EnterpriseHomePageQuery';
import type {
  ProgramSkillsetsQuery as ProgramSkillsetsQueryData,
  ProgramSkillsetsQueryVariables,
  ProgramSkillsetsQuery_EnterpriseTargetSkillProfileSummariesV1Resource_multiGet_elements as SkillSet,
} from 'bundles/program-home/components/enterprise-home/__generated__/ProgramSkillsetsQuery';
import SkillSetsContainerV2, {
  SkillSetsContainerV2Placeholder,
} from 'bundles/program-home/components/single-program/SkillSetsContainerV2';

import _t from 'i18n!nls/program-home';

type PropsFromCaller = {
  userId: number;
  thirdPartyOrganizationId: string;
  enterprisePrograms: EnterpriseProgram[];
  displayLimit?: number;
};

type PropsFromGraphql = {
  loading: boolean;
  error: boolean;
  skillSetById?: Record<string, SkillSet>;
};

export type Props = PropsFromCaller & PropsFromGraphql;

const collectionResponsiveConfig = {
  xs: { displayLimit: 1 },
  sm: { displayLimit: 1 },
  md: { displayLimit: 2 },
  lg: { displayLimit: 2 },
  xl: { displayLimit: 2 },
  xxl: { displayLimit: 2 },
};

export const MultiProgramSkillSetTabContent: React.FC<Props> = ({
  skillSetById,
  displayLimit,
  error,
  loading,
  userId,
  thirdPartyOrganizationId,
  enterprisePrograms,
}: Props) => {
  const theme = useTheme();

  const styles = {
    container: css`
      margin: 2.75rem auto 0;
      padding: 0 12px 2.75rem;
    `,
    programs: css`
      margin-top: ${theme.spacing(48)};
    `,
    programTitle: css`
      border-bottom: 1px solid ${theme.palette.gray[500]};
      padding-bottom: ${theme.spacing(12)};
      margin-bottom: ${theme.spacing(16)};
    `,
    programContent: css`
      margin-top: ${theme.spacing(48)};
    `,
  };

  if (error) {
    return (
      <PageGridContainer className="rc-MultiProgramSkillSetTabContent" css={styles.container}>
        <Grid item xs={12}>
          <InlineNotification severity="error">
            {_t('Sorry! Something went wrong. Please refresh the page.')}
          </InlineNotification>
        </Grid>
      </PageGridContainer>
    );
  }

  if (loading || !skillSetById) {
    return (
      <PageGridContainer className="rc-MultiProgramSkillSetTabContent" css={styles.container}>
        <Grid item xs={12}>
          <MultiProgramSkillsetWarningBanner userId={userId} thirdPartyOrganizationId={thirdPartyOrganizationId} />
          {enterprisePrograms
            .filter((enterpriseProgram) => enterpriseProgram.metadata.linkedTargetSkillProfileIds.length > 0)
            .map((enterpriseProgram) => (
              <div key={enterpriseProgram.id} css={styles.programs}>
                <Heading defaultLevel={2} className="program-name" css={styles.programTitle}>
                  {enterpriseProgram.metadata.name}
                </Heading>
                <Section initialLevel={3}>
                  <div css={styles.programContent}>
                    <SkillSetsContainerV2Placeholder skillSetLength={displayLimit} />
                  </div>
                </Section>
              </div>
            ))}
        </Grid>
      </PageGridContainer>
    );
  }

  return (
    <PageGridContainer className="rc-MultiProgramSkillSetTabContent" css={styles.container}>
      <Grid item xs={12}>
        <MultiProgramSkillsetWarningBanner userId={userId} thirdPartyOrganizationId={thirdPartyOrganizationId} />
        {enterprisePrograms
          .filter((enterpriseProgram) => {
            const ids = enterpriseProgram.metadata.linkedTargetSkillProfileIds;
            return ids.length > 0 && ids.some((id) => skillSetById[id]);
          })
          .map((enterpriseProgram) => {
            const skillsets = enterpriseProgram.metadata.linkedTargetSkillProfileIds
              .map((id) => skillSetById[id])
              .filter(Boolean);

            return (
              <div key={enterpriseProgram.id} css={styles.programs}>
                <Heading defaultLevel={2} css={styles.programTitle}>
                  {enterpriseProgram.metadata.name}
                </Heading>
                <Section initialLevel={3}>
                  <div css={styles.programContent}>
                    <SkillSetsContainerV2
                      skillsets={skillsets}
                      programSlug={enterpriseProgram.metadata.slug}
                      programId={enterpriseProgram.id}
                      displayLimit={displayLimit}
                    />
                  </div>
                </Section>
              </div>
            );
          })}
      </Grid>
    </PageGridContainer>
  );
};

export const ProgramThirdPartyOrgTargetSkillProfileQuery = gql`
  query ProgramSkillsetsQuery($ids: [String!]!) {
    EnterpriseTargetSkillProfileSummariesV1Resource {
      multiGet(ids: $ids) {
        elements {
          id
          slug
          title
          description {
            ... on EnterpriseTargetSkillProfileSummariesV1_cmlMember {
              cml {
                dtdId
                value
              }
            }
          }
          targetSkillProficiencies {
            skillId
            targetProficiency
            skillName
          }
        }
      }
    }
  }
`;

const MultiProgramSkillSetTabContentHookProxy = (props: Props) => <MultiProgramSkillSetTabContent {...props} />;

export const enhance = compose<Props, PropsFromCaller>(
  withProps({ responsiveConfig: collectionResponsiveConfig }),
  withResponsiveConfig,
  graphql<PropsFromCaller, ProgramSkillsetsQueryData, ProgramSkillsetsQueryVariables, PropsFromGraphql>(
    ProgramThirdPartyOrgTargetSkillProfileQuery,
    {
      options: ({ enterprisePrograms }) => ({
        ssr: false,
        variables: {
          ids: union(flatMap(enterprisePrograms, (x) => x.metadata.linkedTargetSkillProfileIds)),
        },
      }),
      props: ({ data }): PropsFromGraphql => {
        const loading = data?.loading ?? true;
        const error = !!data?.error;
        const skillSetById = keyBy(
          filterExistsOrDefault(data?.EnterpriseTargetSkillProfileSummariesV1Resource?.multiGet?.elements),
          (x) => x.id
        );
        return { skillSetById, loading, error };
      },
    }
  )
);

export default enhance(MultiProgramSkillSetTabContentHookProxy);
